<template>
  <b-modal
    v-model="model"
    size="md"
    modal-class="modal-bb-squared modal-pitch"
    hide-footer
    hide-header
  >
    <button class="close" @click="model = false"></button>

    <h4 class="mb-40">Send your pitch directly to the investor</h4>

    <AlertSlideUpDown :show="!!error">{{ error }}</AlertSlideUpDown>

    <form @submit.prevent="onSubmit">
      <FormItem
        invalid-message="Please enter full name"
        :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
        required
      >
        <b-form-input
          v-model="form.name"
          :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
          placeholder="Full name"
        />
      </FormItem>

      <FormItem
        invalid-message="Please enter startup name"
        :state="$v.form.startup.$dirty ? !$v.form.startup.$error : null"
        required
      >
        <b-form-input
          v-model="form.startup"
          :state="$v.form.startup.$dirty ? !$v.form.startup.$error : null"
          placeholder="Startup name"
        />
      </FormItem>

      <FormItem
        invalid-message="Please enter phone number"
        :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
        required
      >
        <b-form-input
          v-model="form.phone"
          :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
          placeholder="Phone number"
        />
      </FormItem>

      <FormItem
        invalid-message="Please enter email address"
        :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
        required
      >
        <b-form-input
          v-model="form.email"
          :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
          placeholder="Email address"
        />
      </FormItem>

      <div class="divider"></div>

      <FormItem>
        <b-form-input
          v-model="form.link"
          placeholder="Link to demo (optional)"
        />
      </FormItem>

      <FormItem
        invalid-message="Please add a short description of the project"
        :state="$v.form.pitchText.$dirty ? !$v.form.pitchText.$error : null"
        required
        class="form-item__textarea mb-40"
      >
        <template slot="description">
          <FormInputLengthCounter
            :min="100"
            :max="500"
            :text="form.pitchText"
          />
        </template>

        <template>
          <b-form-textarea
            v-model="form.pitchText"
            rows="4"
            no-resize
            placeholder="Please add a short description of the project"
            @blur="$v.form.pitchText.$touch()"
          />
        </template>
      </FormItem>

      <FormItem
        invalid-message="Please add a pitch deck"
        :state="$v.form.pitch.$dirty ? !$v.form.pitch.$error : null"
        required
      >
        <FormAttachment
          v-model="form.pitch"
          type="document"
          label="Attach a pitch deck (max 15 MB)"
        />
      </FormItem>

      <b-button class="mt-30" type="submit" variant="primary" size="lg"
        >Send</b-button
      >
    </form>

    <p class="mt-10">
      By sending the request you confirm that you accept our
      <b-link to="/terms" class="btn-link" target="_blank"
        >Terms of Service</b-link
      >
      and
      <b-link to="/privacy-policy" class="btn-link" target="_blank"
        >Privacy Policy</b-link
      >
    </p>
  </b-modal>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'
import modalMixin from '@/mixins/modal'
import FormItem from '@/components/form/FormItem/index'
import AlertSlideUpDown from '@/components/AlertSlideUpDown'
import FormInputLengthCounter from '@/components/form/FormInputLengthCounter'
import FormAttachment from '@/components/form/FormAttachment'

const initial = {
  name: undefined,
  startup: undefined,
  phone: undefined,
  email: undefined,
  link: undefined,
  pitchText: undefined,
  pitch: undefined
}

export default {
  name: 'FundingPitchModal',
  components: {
    FormItem,
    AlertSlideUpDown,
    FormInputLengthCounter,
    FormAttachment
  },
  mixins: [modalMixin, validationMixin],
  props: {
    investor: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isSending: false,
      error: '',
      form: {
        ...initial
      }
    }
  },
  methods: {
    async onSubmit() {
      if (this.isSending) {
        return
      }

      this.$v.form.$touch()

      if (this.$v.form.$error) {
        return
      }

      this.isSending = true
      this.error = ''

      const fd = new FormData()

      for (let name in this.form) {
        fd.append(name, this.form[name])
      }

      try {
        await this.$axios.post(this.investor.pitchUrl, fd)

        this.$gtm.push({
          event: 'ga.event',
          category: 'form',
          action: 'PitchSubmit',
          label: this.investor.key
        })
        this.$gtm.push({ event: 'fb', fbEvent: 'PitchSubmit' })

        this.model = false
        this.resetForm()
      } catch (err) {
        this.error = err.message
      }

      this.$emit('submitted')

      this.isSending = false
    },
    resetForm() {
      this.form = { ...initial }
      this.$v.form.$reset()
    }
  },
  validations: {
    form: {
      name: {
        required
      },
      startup: {
        required
      },
      phone: {
        required
      },
      email: {
        required,
        email
      },
      pitchText: {
        required
      },
      pitch: {
        required
      }
    }
  }
}
</script>

<style lang="scss">
.modal-pitch {
  h4 {
    font-size: rem(24px);
  }

  .form-item {
    position: relative;
    margin-bottom: rem(12px);

    textarea {
      padding-right: rem(70px);
    }

    &__description {
      position: absolute;
      bottom: 0;
      right: 0;
      margin: rem(14px) rem(16px);
    }

    &__textarea .form-item__error {
      position: absolute;
      top: 100%;
    }
  }

  .divider {
    margin: rem(20px) 0;
    height: 1px;
    background: rgba(135, 135, 135, 0.2);
  }

  [type='submit'] {
    width: 100%;
  }

  p {
    font-size: rem(14px);
    line-height: rem(20px);
  }
}
</style>
